<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Geofence List -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/geofence', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Geofences</b>
      </a>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">{{ loadingMessage }}</p>
    </div>

    <!-- Geofence Form -->
    <div class="schedule-form mt-2">
      <div class="card">
        <div class="card-body">

          <!-- Geofence Name -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-tag"></i> Geofence Name <span class="text-danger">*</span>
            </label>
            <input
              ref="geofenceNameInput"
              type="text"
              class="form-control form-control-sm"
              v-model="currentGeofence.name"
              placeholder="e.g., Home, School, Grandma's House"
              :class="{ 'is-invalid': errors.name }"
              maxlength="50"
            />
            <small class="text-muted">Give your geofence a descriptive name</small>
            <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
          </div>

          <!-- Place Search -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-search"></i> Search Location <span class="text-danger">*</span>
            </label>
            <input
              ref="autocomplete"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search for an address or place..."
              :class="{ 'is-invalid': errors.location }"
            />
            <small class="text-muted">Search and select a location, or drag the marker on the map</small>
            <div v-if="errors.location" class="invalid-feedback">{{ errors.location }}</div>
          </div>

          <!-- Google Map -->
          <div class="map-section-wrapper mb-3">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-map"></i> Map Location
            </label>
            <div class="map-wrapper">
              <!-- Loading/Error overlay -->
              <div v-if="isLoadingMap || !googleMapsApiKey || googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY'" class="map-overlay">
                <div v-if="isLoadingMap" class="map-loading">
                  <div class="spinner-border text-primary"></div>
                  <p>Loading map...</p>
                </div>
                <div v-else class="map-loading">
                  <i class="fas fa-key" style="font-size: 3rem; color: #f59e0b; margin-bottom: 15px;"></i>
                  <h6 class="font-weight-bold">Google Maps API Key Required</h6>
                  <p class="text-muted mb-3">Please configure your Google Maps API key to use map features.</p>
                  <small class="text-muted">
                    <strong>Steps:</strong><br>
                    1. Get API key from <a href="https://console.cloud.google.com/" target="_blank">Google Cloud Console</a><br>
                    2. Enable Maps JavaScript API & Places API<br>
                    3. Replace <code>googleMapsApiKey</code> in the code
                  </small>
                </div>
              </div>
              <!-- Map container (separate from Vue reactivity) -->
              <div ref="map" class="map-container"></div>
            </div>
            <small class="text-muted d-block mt-2">
              <i class="fas fa-info-circle"></i> 
              Drag the marker to adjust the location. The blue circle shows the geofence radius.
            </small>
          </div>

          <!-- Address (Read-only) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-map-pin"></i> Address
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="currentGeofence.location.address"
              readonly
              placeholder="Address will appear here after selecting location"
            />
          </div>

          <!-- Coordinates (Read-only) -->
          <div class="form-group mb-3 text-left">
            <div class="row">
              <div class="col-6">
                <label class="font-weight-bold"><small>Latitude</small></label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :value="currentGeofence.location.latitude.toFixed(6)"
                  readonly
                />
              </div>
              <div class="col-6">
                <label class="font-weight-bold"><small>Longitude</small></label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :value="currentGeofence.location.longitude.toFixed(6)"
                  readonly
                />
              </div>
            </div>
          </div>

          <!-- Radius Slider -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-circle-notch"></i> Radius: <span class="text-primary">{{ currentGeofence.radius }}m</span>
            </label>
            <input
              type="range"
              class="custom-range"
              v-model.number="currentGeofence.radius"
              min="50"
              max="5000"
              step="10"
              @input="updateCircleRadius"
            />
            <div class="d-flex justify-content-between">
              <small class="text-muted">50m</small>
              <small class="text-muted">2.5km</small>
              <small class="text-muted">5km</small>
            </div>
          </div>

          <!-- Icon Selector -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-icons"></i> Icon
            </label>
            <div class="icon-selector-grid">
              <button
                v-for="icon in iconOptions"
                :key="icon.value"
                type="button"
                class="icon-option-btn"
                :class="{ 'active': currentGeofence.icon === icon.value }"
                @click="selectIcon(icon.value)"
              >
                <i :class="icon.iconClass"></i>
                <span>{{ icon.label }}</span>
              </button>
            </div>
          </div>

          <!-- Color Picker -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-palette"></i> Color
            </label>
            <div class="color-picker-grid">
              <button
                v-for="color in colorOptions"
                :key="color"
                type="button"
                class="color-option-btn"
                :class="{ 'active': currentGeofence.color === color }"
                :style="{ background: color }"
                @click="selectColor(color)"
              >
                <i v-if="currentGeofence.color === color" class="fas fa-check"></i>
              </button>
              <label class="custom-color-wrapper">
                <input
                  type="color"
                  v-model="currentGeofence.color"
                  @change="updateCircleColor"
                  class="custom-color-input"
                />
                <span class="custom-color-label">Custom</span>
              </label>
            </div>
          </div>

          <!-- Notification Settings -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-bell"></i> Notifications
            </label>
            <div class="notification-toggles">
              <label class="notification-toggle-item">
                <input
                  type="checkbox"
                  v-model="currentGeofence.notifyOnEntry"
                />
                <span class="notification-text">
                  <i class="fas fa-arrow-right text-success"></i> Notify when entering
                </span>
              </label>
              <label class="notification-toggle-item">
                <input
                  type="checkbox"
                  v-model="currentGeofence.notifyOnExit"
                />
                <span class="notification-text">
                  <i class="fas fa-arrow-left text-danger"></i> Notify when leaving
                </span>
              </label>
            </div>
          </div>

          <!-- Active Status -->
          <div class="form-group mb-3 text-left">
            <label class="notification-toggle-item">
              <input
                type="checkbox"
                v-model="currentGeofence.isActive"
              />
              <span class="notification-text font-weight-bold">
                <i class="fas fa-power-off"></i> Geofence Active
              </span>
            </label>
          </div>

        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons mt-3">
      <button
        class="btn btn-primary btn-lg btn-block"
        @click="saveGeofence"
        :disabled="isSaving"
      >
        <span v-if="isSaving">
          <i class="fas fa-spinner fa-spin mr-1"></i>Saving...
        </span>
        <span v-else>
          <i class="fas fa-save mr-1"></i>{{ isEditMode ? 'Update' : 'Create' }} Geofence
        </span>
      </button>
      <button
        class="btn btn-secondary btn-lg btn-block"
        @click="$router.push({ path: '/geofence', query: { profileId: profileId, serialNumber: serialNumber } })"
        :disabled="isSaving"
      >
        <i class="fas fa-times mr-1"></i>Cancel
      </button>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import GeofenceService from '@/services/GeofenceService';
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";

export default {
  name: 'CreateGeofence',
  components: {
    KidHeader,
    KidFooter
  },
  data() {
    return {
      profileId: this.$route.query.profileId || '',
      serialNumber: this.$route.query.serialNumber || '',
      geofenceId: this.$route.query.geofenceId || null,
      isEditMode: this.$route.query.edit === 'true',
      
      userLocation: null,
      userCountry: null,
      userCity: null,
      detectedLocation: false,
      currentGeofence: {
        name: '',
        location: {
          latitude: 51.5074,
          longitude: -0.1278,
          address: '',
          placeId: null
        },
        radius: 100,
        icon: 'home',
        color: '#10b981',
        notifyOnEntry: true,
        notifyOnExit: true,
        isActive: true
      },

      map: null,
      marker: null,
      circle: null,
      autocomplete: null,
      geocoder: null,
      
      isLoadingMap: true,
      isLoading: false,
      isSaving: false,
      loadingMessage: '',
      
      errors: {},

      iconOptions: [
        { value: 'home', label: 'Home', iconClass: 'fas fa-home' },
        { value: 'school', label: 'School', iconClass: 'fas fa-school' },
        { value: 'hospital', label: 'Hospital', iconClass: 'fas fa-hospital' },
        { value: 'store', label: 'Store', iconClass: 'fas fa-shopping-cart' },
        { value: 'friend', label: 'Friend', iconClass: 'fas fa-user-friends' },
        { value: 'family', label: 'Family', iconClass: 'fas fa-users' },
        { value: 'other', label: 'Other', iconClass: 'fas fa-map-marker-alt' }
      ],

      // Google Maps API Key - REPLACE THIS!
      googleMapsApiKey: process.env.VUE_APP_GOOGLE_MAP_KEY,

      colorOptions: [
        '#10b981', // Green
        '#3b82f6', // Blue
        '#8b5cf6', // Purple
        '#ef4444', // Red
        '#f59e0b', // Orange
        '#ec4899', // Pink
        '#14b8a6', // Teal
        '#6366f1'  // Indigo
      ]
    };
  },
  mounted() {
    // Use $nextTick to ensure DOM is ready
    this.$nextTick(() => {
      // Focus on name input after a delay
      setTimeout(() => {
        if (this.$refs.geofenceNameInput) {
          this.$refs.geofenceNameInput.focus();
          if (this.isEditMode) {
            this.$refs.geofenceNameInput.select();
          }
        }
      }, 150);

      // Load geofence data FIRST if editing
      if (this.isEditMode && this.geofenceId) {
        this.loadGeofenceForEdit().then(() => {
          // After geofence data is loaded, THEN load the map with correct coordinates
          setTimeout(() => {
            this.loadGoogleMaps();
          }, 100);
        });
      } else {
        // For new geofence, load map normally
        setTimeout(() => {
          this.loadGoogleMaps();
        }, 100);
      }
    });
  },
  
  beforeDestroy() {
    // Cleanup: Remove event listeners if any
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
    if (this.marker) {
      google.maps.event.clearInstanceListeners(this.marker);
    }
  },
  methods: {
    loadGoogleMaps() {
      // Check if API key is set
      if (!this.googleMapsApiKey || this.googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
        this.isLoadingMap = false;
        console.warn('Google Maps API key not configured');
        this.$toast.warning('Google Maps API key not configured. Map features disabled.');
        return;
      }

      // Check if already loaded
      if (window.google && window.google.maps) {
        this.initMap();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        // Wait for existing script to load
        const checkGoogleMaps = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogleMaps);
            this.initMap();
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkGoogleMaps);
          if (!window.google || !window.google.maps) {
            this.isLoadingMap = false;
            this.$toast.error('Google Maps failed to load');
          }
        }, 10000);
        return;
      }

      // Use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        try {
          // Create script element
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
          script.async = true;
          script.defer = true;
          
          script.onload = () => {
            // Wait for Google Maps to be fully available
            const checkGoogleMaps = setInterval(() => {
              if (window.google && window.google.maps) {
                clearInterval(checkGoogleMaps);
                this.initMap();
              }
            }, 50);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkGoogleMaps);
              if (!window.google || !window.google.maps) {
                this.isLoadingMap = false;
                this.$toast.error('Google Maps failed to initialize');
              }
            }, 5000);
          };
          
          script.onerror = () => {
            this.isLoadingMap = false;
            this.$toast.error('Failed to load Google Maps. Please check your API key.');
          };

          // Append script
          if (document.head) {
            document.head.appendChild(script);
          } else {
            console.error('document.head not available');
            this.isLoadingMap = false;
            this.$toast.error('Cannot load Google Maps');
          }
        } catch (error) {
          console.error('Error loading Google Maps script:', error);
          this.isLoadingMap = false;
          this.$toast.error('Error loading Google Maps');
        }
      }, 200); // Wait 200ms for DOM to be fully ready
    },

    initMap() {
      try {
        // Safety check: ensure map ref exists
        if (!this.$refs.map) {
          console.error('Map container ref not found');
          this.isLoadingMap = false;
          return;
        }

        // Safety check: ensure Google Maps is loaded
        if (!window.google || !window.google.maps) {
          console.error('Google Maps not loaded');
          this.isLoadingMap = false;
          return;
        }

        // Use current geofence coordinates (will have loaded data if editing)
        const centerLat = this.currentGeofence.location.latitude;
        const centerLng = this.currentGeofence.location.longitude;

        this.map = new google.maps.Map(this.$refs.map, {
          center: {
            lat: centerLat,
            lng: centerLng
          },
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true
        });

        this.geocoder = new google.maps.Geocoder();

        this.marker = new google.maps.Marker({
          position: { lat: centerLat, lng: centerLng },
          map: this.map,
          draggable: true,
          animation: google.maps.Animation.DROP,
          title: 'Drag to adjust location'
        });

        this.circle = new google.maps.Circle({
          map: this.map,
          center: { lat: centerLat, lng: centerLng },
          radius: this.currentGeofence.radius,
          fillColor: this.currentGeofence.color,
          fillOpacity: 0.2,
          strokeColor: this.currentGeofence.color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          editable: false
        });

        this.circle.bindTo('center', this.marker, 'position');

        this.marker.addListener('dragend', (event) => {
          this.onMarkerDragEnd(event.latLng);
        });

        this.map.addListener('click', (event) => {
          this.marker.setPosition(event.latLng);
          this.onMarkerDragEnd(event.latLng);
        });

        this.initAutocomplete();

        // Only reverse geocode if no address exists (new geofence)
        if (!this.currentGeofence.location.address) {
          this.reverseGeocode(this.map.getCenter());
        }

        this.isLoadingMap = false;

      } catch (error) {
        console.error('Error initializing map:', error);
        this.isLoadingMap = false;
        this.$toast.error('Error loading map: ' + error.message);
      }
    },

    initAutocomplete() {
      if (!this.$refs.autocomplete) return;

      this.autocomplete = new google.maps.places.Autocomplete(
        this.$refs.autocomplete,
        {
          types: ['geocode', 'establishment'],
          fields: ['geometry', 'formatted_address', 'place_id', 'name']
        }
      );

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) {
          this.$toast.error('No location found for this place');
          return;
        }

        const location = place.geometry.location;
        this.map.setCenter(location);
        this.map.setZoom(16);
        this.marker.setPosition(location);

        this.currentGeofence.location.latitude = location.lat();
        this.currentGeofence.location.longitude = location.lng();
        this.currentGeofence.location.address = place.formatted_address || place.name;
        this.currentGeofence.location.placeId = place.place_id;

        this.errors.location = '';
      });
    },

    onMarkerDragEnd(latLng) {
      this.currentGeofence.location.latitude = latLng.lat();
      this.currentGeofence.location.longitude = latLng.lng();
      this.reverseGeocode(latLng);
    },

    reverseGeocode(latLng) {
      if (!this.geocoder) return;

      this.geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.currentGeofence.location.address = results[0].formatted_address;
          this.currentGeofence.location.placeId = results[0].place_id;
        }
      });
    },

    updateCircleRadius() {
      if (this.circle) {
        this.circle.setRadius(this.currentGeofence.radius);
      }
    },

    updateCircleColor() {
      if (this.circle) {
        this.circle.setOptions({
          fillColor: this.currentGeofence.color,
          strokeColor: this.currentGeofence.color
        });
      }
    },

    selectIcon(icon) {
      this.currentGeofence.icon = icon;
    },

    selectColor(color) {
      this.currentGeofence.color = color;
      this.updateCircleColor();
    },

    validateForm() {
      this.errors = {};

      if (!this.currentGeofence.name.trim()) {
        this.errors.name = 'Please enter a geofence name';
      }

      if (!this.currentGeofence.location.address) {
        this.errors.location = 'Please select a location';
      }

      return Object.keys(this.errors).length === 0;
    },

    async saveGeofence() {
      if (!this.validateForm()) {
        this.$toast.error('Please fix the errors');
        return;
      }

      this.isSaving = true;

      try {
        // Get parent email from store or localStorage
        let parentEmail = '';
        try {
          parentEmail = this.$store?.state?.user?.email || '';
        } catch (e) {
          // If $store is not available, try localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          parentEmail = user.email || '';
        }

        const data = {
          profileId: this.profileId,
          serialNumber: this.serialNumber,
          parentEmail: parentEmail,
          name: this.currentGeofence.name.trim(),
          location: this.currentGeofence.location,
          radius: this.currentGeofence.radius,
          icon: this.currentGeofence.icon,
          color: this.currentGeofence.color,
          notifyOnEntry: this.currentGeofence.notifyOnEntry,
          notifyOnExit: this.currentGeofence.notifyOnExit,
          isActive: this.currentGeofence.isActive
        };

        let response;

        if (this.isEditMode) {
          response = await GeofenceService.updateGeofence(this.geofenceId, data);
        } else {
          response = await GeofenceService.createGeofence(data);
        }

        if (response.data.success) {
          this.$toast.success(
            this.isEditMode ? 'Geofence updated successfully' : 'Geofence created successfully'
          );

          setTimeout(() => {
            this.$router.push({
              path: '/geofence',
              query: {
                profileId: this.profileId,
                serialNumber: this.serialNumber
              }
            });
          }, 500);
        } else {
          this.$toast.error(response.data.message || 'Failed to save geofence');
        }
      } catch (error) {
        console.error('Error saving geofence:', error);
        
        if (error.response?.data?.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error('Failed to save geofence');
        }
      } finally {
        this.isSaving = false;
      }
    },

    async loadGeofenceForEdit() {
      this.isLoading = true;
      this.loadingMessage = 'Loading geofence...';

      try {
        const response = await GeofenceService.getGeofenceById(
          this.geofenceId,
          this.profileId,
          this.serialNumber
        );

        if (response.data.success) {
          const geofence = response.data.data;

          // Update current geofence with loaded data
          this.currentGeofence = {
            name: geofence.name,
            location: {
              latitude: geofence.location.latitude,
              longitude: geofence.location.longitude,
              address: geofence.location.address,
              placeId: geofence.location.placeId
            },
            radius: geofence.radius,
            icon: geofence.icon,
            color: geofence.color,
            notifyOnEntry: geofence.notifyOnEntry,
            notifyOnExit: geofence.notifyOnExit,
            isActive: geofence.isActive
          };

          // Map will be initialized after this with the correct coordinates
        }
      } catch (error) {
        console.error('Error loading geofence:', error);
        this.$toast.error('Failed to load geofence details');
        
        setTimeout(() => {
          this.$router.push({
            path: '/geofence',
            query: {
              profileId: this.profileId,
              serialNumber: this.serialNumber
            }
          });
        }, 2000);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 15px;
  font-size: 1rem;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  color: white;
}

/* Form Container */
.schedule-form .card {
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: none;
}

.card-body {
  padding: 25px;
}

/* Form Groups */
.form-group label {
  font-size: 0.95rem;
  color: #374151;
  margin-bottom: 8px;
}

.form-control {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  transition: all 0.2s;
}

.form-control:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  outline: none;
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.invalid-feedback {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
}

/* Map Section */
.map-section-wrapper {
  margin-bottom: 20px;
}

.map-wrapper {
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.map-container {
  width: 100%;
  height: 100%;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f9fafb;
  z-index: 10;
  pointer-events: none;
}

.map-overlay.active {
  pointer-events: all;
}

.map-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  text-align: center;
}

.map-loading h6 {
  color: #1f2937;
  margin: 0;
}

.map-loading p {
  color: #6b7280;
  margin: 0;
}

.map-loading code {
  background: #fee2e2;
  color: #991b1b;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

/* Range Slider */
.custom-range {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

.custom-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #10b981;
  cursor: pointer;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #10b981;
  cursor: pointer;
  border-radius: 50%;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Icon Selector Grid */
.icon-selector-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.icon-option-btn {
  padding: 15px 10px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #6b7280;
}

.icon-option-btn i {
  font-size: 1.5rem;
}

.icon-option-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.icon-option-btn.active {
  background: #d1fae5;
  border-color: #10b981;
  color: #10b981;
}

/* Color Picker Grid */
.color-picker-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.color-option-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.color-option-btn:hover {
  transform: scale(1.1);
}

.color-option-btn.active {
  border-color: #1f2937;
  box-shadow: 0 0 0 2px white, 0 0 0 4px #1f2937;
}

.custom-color-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.custom-color-input {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
}

.custom-color-label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Notification Toggles */
.notification-toggles {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notification-toggle-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  transition: all 0.2s;
}

.notification-toggle-item:hover {
  background: #f3f4f6;
}

.notification-toggle-item input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #10b981;
}

.notification-text {
  font-size: 0.95rem;
  color: #374151;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 30px;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 768px) {
  .card-body {
    padding: 20px;
  }

  .map-wrapper {
    height: 300px;
  }

  .icon-selector-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
}
</style>